import React, { useState } from 'react';
import { Drawer, Dropdown, Button, Tooltip, Skeleton, Typography, Modal } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { Table } from '@src/components/common/Table';
import useGetScheduleList, { ScheduleListItem } from './hooks/useGetScheduleList';
import { useDeleteSchedule } from './hooks/useDeleteSchedule';
import { useUpdateScheduleStatus } from './hooks/useUpdateScheduleStatus';
import ScheduleAnalysisDrawer from '../ScheduleSavedAnalysis/ScheduleAnalysisDrawer';
import { TruncatedCell, CapitalizedCell, ScheduleTableWrapper } from './styled';
import { checkIsAdminUser } from '@src/globalStore/profileStore';

const { Text } = Typography;

const ManageSchedules = ({
  open,
  setOpen,
  onRequestOpenManageSchedules,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  onRequestOpenManageSchedules?: (opts?: { scheduleId?: number }) => void;
}) => {
  const { data, isFetching, isError } = useGetScheduleList({ open });
  const [editItem, setEditItem] = useState<ScheduleListItem | null>(null);
  const deleteMutation = useDeleteSchedule();
  const statusMutation = useUpdateScheduleStatus();
  const isAdmin = checkIsAdminUser();
  const handleClose = () => {
    setOpen(false);
    setEditItem(null);
  };

  const handleEdit = (row: ScheduleListItem) => {
    setEditItem(row);
  };

  const handleDelete = (scheduleId: number) => {
    Modal.confirm({
      title: 'Delete this Schedule?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => deleteMutation.mutateAsync(scheduleId).catch(() => {}),
    });
  };

  const handleToggleStatus = (scheduleId: number, enable: number) => {
    statusMutation.mutate({ scheduleId, enable });
  };

  const handleDrawerClose = () => {
    setEditItem(null);
  };

  const handleRequestOpenManageSchedules = (opts?: { scheduleId?: number }) => {
    setEditItem(null);
    onRequestOpenManageSchedules?.(opts);
  };

  const items = (rowData) => [
    {
      key: 'edit',
      label: 'Edit',
      onClick: () => handleEdit(rowData),
    },
    {
      key: 'toggle_status',
      label: rowData.is_enabled ? 'Disable' : 'Enable',
      onClick: () => handleToggleStatus(rowData.schedule_id, rowData.is_enabled ? 0 : 1),
    },
    {
      key: 'delete',
      label: 'Delete',
      danger: true,
      onClick: () => handleDelete(rowData.schedule_id),
    },
  ];

  const columns = [
    {
      key: 'query',
      dataKey: 'query',
      title: 'Saved Analysis Query',
      width: 250,
      flexGrow: 1,
      cellRenderer: ({ cellData }: { cellData: string }) => (
        <Tooltip title={cellData} placement="topLeft">
          <TruncatedCell>{cellData}</TruncatedCell>
        </Tooltip>
      ),
    },
    {
      key: 'frequency',
      dataKey: 'frequency',
      title: 'Frequency',
      width: 100,
      cellRenderer: ({ cellData }: { cellData: string }) => (
        <CapitalizedCell>{cellData}</CapitalizedCell>
      ),
    },
    {
      key: 'run_time',
      dataKey: 'run_time',
      title: 'Run Time',
      width: 100,
      cellRenderer: ({ cellData }: { cellData: string | null }) => <span>{cellData || '-'}</span>,
    },
    {
      key: 'week_day',
      dataKey: 'week_day',
      title: 'Weekday',
      width: 110,
      cellRenderer: ({ rowData }: { rowData: ScheduleListItem }) => (
        <span>{rowData.frequency === 'weekly' ? rowData.week_day || '-' : '-'}</span>
      ),
    },
    {
      key: 'created_by_username',
      dataKey: 'created_by_username',
      title: 'Created By',
      width: 130,
      cellRenderer: ({ cellData }: { cellData: string }) => (
        <Tooltip title={cellData} placement="topLeft">
          <span>{cellData}</span>
        </Tooltip>
      ),
    },
    {
      key: 'subscribers',
      dataKey: 'subscribers',
      title: 'Subscribers',
      width: 200,
      cellRenderer: ({ cellData }: { cellData: string[] }) => {
        const text = cellData?.length ? cellData.join(', ') : '-';
        return (
          <Tooltip title={text} placement="topLeft">
            <TruncatedCell>{text}</TruncatedCell>
          </Tooltip>
        );
      },
    },
    {
      key: 'updated_by_username',
      dataKey: 'updated_by_username',
      title: 'Updated By',
      width: 130,
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 80,
      cellRenderer: ({ rowData }: { rowData: ScheduleListItem }) => {
        return (
          <Dropdown menu={{ items: items(rowData) }} trigger={['click']} disabled={!isAdmin}>
            <Button type="text" icon={<MoreOutlined />} size="small" />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <Drawer
        title="Manage Schedules"
        placement="left"
        closable={true}
        onClose={handleClose}
        open={open}
        width={1100}
        destroyOnHidden={true}
      >
        {isFetching ? (
          <Skeleton active />
        ) : isError ? (
          <Text type="danger">Failed to load schedules</Text>
        ) : data.length === 0 ? (
          <Text type="secondary">No schedules found</Text>
        ) : (
          <ScheduleTableWrapper>
            <Table
              data={data}
              columns={columns}
              rowKey="schedule_id"
              headerHeight={50}
              rowHeight={48}
              rowClassName={({ rowData }: { rowData: ScheduleListItem }) =>
                !rowData.is_enabled ? 'row-disabled' : ''
              }
            />
          </ScheduleTableWrapper>
        )}
      </Drawer>
      <ScheduleAnalysisDrawer
        open={!!editItem}
        onClose={handleDrawerClose}
        analysisItem={editItem ? { id: editItem.saved_analysis_id, query: editItem.query } : null}
        mode="edit"
        onRequestOpenManageSchedules={handleRequestOpenManageSchedules}
        scheduleData={
          editItem
            ? {
                schedule_id: editItem.schedule_id,
                frequency: editItem.frequency,
                run_time: editItem.run_time,
                week_day: editItem.week_day,
                subscribers: editItem.subscribers,
                is_internal: editItem.is_internal,
              }
            : undefined
        }
      />
    </>
  );
};

export default ManageSchedules;
